import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowLeft, SpinnerGap, Envelope, ShieldCheck, WarningCircle, FloppyDisk, PaperPlaneTilt, Check, LockKey, Eye, Image as ImageIcon, Trash } from '@phosphor-icons/react';
import RichTextEditor from '@/components/RichTextEditor';

export default function EmailConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    smtp_host: '', smtp_port: 587, smtp_use_tls: true,
    smtp_username: '', smtp_password: '',
    from_email: '', from_name: '', signature_html: '',
    password_set: false,
  });
  const [msg, setMsg] = useState(null);
  const [mandatoryHtml, setMandatoryHtml] = useState('');
  const [sigImage, setSigImage] = useState({ set: false, bytes: 0, mime: '', bust: 0 });
  const [uploadingSig, setUploadingSig] = useState(false);

  useEffect(() => {
    api.get('/consultor/email-config/signature-preview')
      .then(r => setMandatoryHtml(r.data?.mandatory_html || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/consultor/email-config').then(r => {
      const d = r.data || {};
      setConfig({
        smtp_host: d.smtp_host || '',
        smtp_port: d.smtp_port || 587,
        smtp_use_tls: d.smtp_use_tls ?? true,
        smtp_username: d.smtp_username || '',
        smtp_password: '',  // nunca vem do backend
        from_email: d.from_email || '',
        from_name: d.from_name || '',
        signature_html: d.signature_html || '',
        password_set: !!d.password_set,
      });
      setSigImage({
        set: !!d.signature_image_set,
        bytes: d.signature_image_bytes || 0,
        mime: d.signature_image_mime || '',
        bust: Date.now(),
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const payload = { ...config };
      // Envia password só se o utilizador escreveu alguma
      if (!payload.smtp_password) delete payload.smtp_password;
      const r = await api.post('/consultor/email-config', payload);
      setMsg({ type: 'success', text: r.data?.message || 'Configuração guardada.' });
      setConfig(c => ({ ...c, smtp_password: '', password_set: c.password_set || !!payload.smtp_password }));
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Erro ao guardar' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  const handleUploadSignatureImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setMsg({ type: 'error', text: 'Só são aceites PNG ou JPEG.' });
      setTimeout(() => setMsg(null), 5000);
      return;
    }
    if (file.size > 500 * 1024) {
      setMsg({ type: 'error', text: `Ficheiro demasiado grande (${Math.round(file.size / 1024)}KB). Máximo 500KB.` });
      setTimeout(() => setMsg(null), 5000);
      return;
    }
    setUploadingSig(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/consultor/email-config/signature-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSigImage({
        set: true, bytes: r.data?.bytes || file.size,
        mime: r.data?.mime || file.type, bust: Date.now(),
      });
      setMsg({ type: 'success', text: r.data?.message || 'Imagem guardada.' });
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.detail || 'Erro no upload da imagem' });
    } finally {
      setUploadingSig(false);
      e.target.value = ''; // permite re-upload do mesmo ficheiro
      setTimeout(() => setMsg(null), 5000);
    }
  };

  const handleDeleteSignatureImage = async () => {
    if (!window.confirm('Remover a imagem de assinatura? Os emails voltam a usar a assinatura textual por defeito.')) return;
    try {
      await api.delete('/consultor/email-config/signature-image');
      setSigImage({ set: false, bytes: 0, mime: '', bust: Date.now() });
      setMsg({ type: 'success', text: 'Imagem removida.' });
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.detail || 'Erro a remover' });
    } finally {
      setTimeout(() => setMsg(null), 5000);
    }
  };


  const handleTest = async () => {
    setTesting(true);
    setMsg(null);
    // Permite ao consultor testar contra um endereço diferente (útil
    // porque muitos servidores fazem drop silencioso a emails que a
    // mesma conta envia para si própria).
    const alt = window.prompt(
      'Endereço para o email de teste?\n\n(Enter = enviar para o teu próprio email de envio;\nou escreve outro email, ex.: o teu Gmail pessoal)',
      config.from_email,
    );
    if (alt === null) { setTesting(false); return; }  // cancelado
    const targetTo = (alt || '').trim();
    try {
      const payload = targetTo && targetTo !== config.from_email ? { to_email: targetTo } : {};
      const r = await api.post('/consultor/email-config/test', payload);
      setMsg({ type: 'success', text: r.data?.message || 'Email de teste enviado.' });
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Erro no envio de teste' });
    } finally {
      setTesting(false);
      setTimeout(() => setMsg(null), 12000);
    }
  };

  const isValidFromEmail = /@solfut\.com$/i.test((config.from_email || '').trim());
  const isConfigured = config.smtp_host && config.smtp_port && config.smtp_username && config.password_set && config.from_email && isValidFromEmail;

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  return (
    <div data-testid="email-config-page" style={{ fontFamily: "'Manrope', sans-serif" }} className="max-w-3xl">
      <button onClick={() => navigate('/perfil')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={16} /> Voltar ao Perfil
      </button>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Configuração de Email
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Credenciais SMTP que o CRM usa para enviar emails às suas leads em seu nome
          </p>
        </div>
        {isConfigured && (
          <span data-testid="config-status-ok" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-50 text-green-800 border border-green-200">
            <ShieldCheck size={14} /> Configurado
          </span>
        )}
      </div>

      {msg && (
        <div data-testid="email-config-msg" className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-start gap-2 border ${
          msg.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'
        }`}>
          {msg.type === 'success' ? <Check size={16} className="flex-shrink-0 mt-0.5" /> : <WarningCircle size={16} className="flex-shrink-0 mt-0.5" />}
          <span className="whitespace-pre-line">{msg.text}</span>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5">
        {/* SMTP host / porta / TLS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#374151] block mb-1">Servidor SMTP *</label>
            <input value={config.smtp_host} onChange={e => setField('smtp_host', e.target.value)}
              data-testid="smtp-host" placeholder="smtp.gmail.com"
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1">Porta *</label>
            <input type="number" value={config.smtp_port || ''} onChange={e => setField('smtp_port', parseInt(e.target.value) || '')}
              data-testid="smtp-port"
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={config.smtp_use_tls}
              onChange={e => setField('smtp_use_tls', e.target.checked)}
              data-testid="smtp-tls"
              className="w-4 h-4 rounded accent-[#0F1829]" />
            <span className="text-sm text-[#374151]">Usar TLS/SSL</span>
          </label>
          <p className="text-[11px] text-[#6B7280]">Recomendado para portas 587 (STARTTLS) e 465 (SSL implícito).</p>
        </div>

        {/* Credenciais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1">Utilizador SMTP *</label>
            <input value={config.smtp_username} onChange={e => setField('smtp_username', e.target.value)}
              data-testid="smtp-username" placeholder="normalmente o email completo"
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
            {config.smtp_username && config.smtp_host && config.smtp_username.toLowerCase() === config.smtp_host.toLowerCase() && (
              <p className="text-xs text-[#B91C1C] mt-1" data-testid="smtp-username-warning">
                ⚠ O utilizador não deve ser o servidor — normalmente é o seu endereço de email completo (ex: nome@solfut.com).
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1 flex items-center gap-1">
              <LockKey size={12} /> Password {config.password_set ? '(deixe vazio para manter)' : '*'}
            </label>
            <input type="password" value={config.smtp_password} onChange={e => setField('smtp_password', e.target.value)}
              data-testid="smtp-password" autoComplete="new-password"
              placeholder={config.password_set ? '••••••••' : ''}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
          </div>
        </div>

        <div className="border-t border-[#F3F4F6] pt-4">
          <h3 className="text-sm font-semibold text-[#111827] mb-3 flex items-center gap-1.5">
            <Envelope size={16} /> Identidade (remetente)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#374151] block mb-1">Email de envio *</label>
              <input value={config.from_email} onChange={e => setField('from_email', e.target.value)}
                data-testid="from-email"
                placeholder="nome@solfut.com"
                className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none ${
                  config.from_email && !isValidFromEmail ? 'border-red-400' : 'border-[#E5E7EB]'
                }`} />
              <p className={`text-[11px] mt-1 ${config.from_email && !isValidFromEmail ? 'text-red-600 font-medium' : 'text-[#6B7280]'}`}
                data-testid="from-email-hint">
                {config.from_email && !isValidFromEmail
                  ? '⚠ Tem de terminar em @solfut.com — não são aceites emails pessoais (gmail, hotmail, etc.).'
                  : 'Obrigatório: tem de ser um email @solfut.com.'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#374151] block mb-1">Nome de exibição</label>
              <input value={config.from_name} onChange={e => setField('from_name', e.target.value)}
                data-testid="from-name" placeholder="Ex: João Silva – IHTP"
                className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
            </div>
          </div>
        </div>

        <div className="border-t border-[#F3F4F6] pt-4">
          <h3 className="text-sm font-semibold text-[#111827] mb-1">Assinatura adicional (opcional)</h3>
          <p className="text-xs text-[#6B7280] mb-2">
            Texto extra anexado antes da assinatura obrigatória (Nome / Consultor I Have the Power / Email / Telemóvel), que é sempre adicionada automaticamente a partir do seu perfil, seguida do aviso legal de confidencialidade (PT + EN).
          </p>
          <RichTextEditor value={config.signature_html}
            onChange={v => setField('signature_html', v)}
            testId="signature-editor"
            placeholder="Ex: links para redes sociais, disclaimer, mensagem promocional…" />
        </div>

        <div className="border-t border-[#F3F4F6] pt-4">
          <h3 className="text-sm font-semibold text-[#111827] mb-1 flex items-center gap-1.5">
            <ImageIcon size={16} /> Imagem de assinatura (opcional)
          </h3>
          <p className="text-xs text-[#6B7280] mb-3">
            Carrega uma imagem (PNG/JPG, ≤500KB) para <strong>substituir a assinatura textual por defeito</strong> (Nome / Consultor / Email / Telemóvel). O aviso legal de confidencialidade continua sempre no fim dos emails.
          </p>
          {sigImage.set && (
            <div className="mb-3 flex items-start gap-3 p-3 border border-[#E5E7EB] rounded-md bg-[#FBFAF7]"
              data-testid="signature-image-preview-wrap">
              <img src={`/api/consultor/email-config/signature-image?t=${sigImage.bust}`}
                alt="Assinatura carregada"
                data-testid="signature-image-preview"
                className="max-h-24 max-w-xs border border-[#E5E7EB] rounded bg-white" />
              <div className="text-xs text-[#6B7280] flex-1">
                <p className="font-medium text-[#111827]">Imagem activa</p>
                <p>{Math.round((sigImage.bytes || 0) / 1024)} KB · {sigImage.mime}</p>
                <button onClick={handleDeleteSignatureImage}
                  data-testid="signature-image-remove"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-rose-600 hover:underline">
                  <Trash size={12} /> Remover imagem
                </button>
              </div>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6] cursor-pointer"
            data-testid="signature-image-upload-label">
            {uploadingSig ? <SpinnerGap size={14} className="animate-spin" /> : <ImageIcon size={14} />}
            {sigImage.set ? 'Substituir imagem' : 'Carregar imagem'}
            <input type="file" accept="image/png,image/jpeg" hidden
              onChange={handleUploadSignatureImage} disabled={uploadingSig}
              data-testid="signature-image-input" />
          </label>
        </div>

        <div className="border-t border-[#F3F4F6] pt-4">
          <h3 className="text-sm font-semibold text-[#111827] mb-1 flex items-center gap-1.5">
            <Eye size={16} /> Pré-visualização da assinatura
          </h3>
          <p className="text-xs text-[#6B7280] mb-2">
            É assim que a assinatura completa aparece no fim de cada email enviado (assinatura adicional + assinatura obrigatória + aviso legal).
          </p>
          <div data-testid="signature-preview"
            className="border border-[#E5E7EB] rounded-md bg-[#FBFAF7] px-4 py-3 overflow-x-auto">
            {config.signature_html && (
              <div style={{ marginTop: 4, fontSize: 14, color: '#374151' }}
                dangerouslySetInnerHTML={{ __html: config.signature_html }} />
            )}
            {sigImage.set ? (
              <div style={{ marginTop: 16 }} data-testid="signature-preview-image">
                <img src={`/api/consultor/email-config/signature-image?t=${sigImage.bust}`}
                  alt="Assinatura" style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #E5E7EB',
                  fontSize: 10, lineHeight: 1.5, color: '#9CA3AF', fontFamily: 'Arial,sans-serif' }}>
                  Aviso legal de confidencialidade (PT + EN) — continua sempre no fim.
                </div>
              </div>
            ) : (
              mandatoryHtml
                ? <div dangerouslySetInnerHTML={{ __html: mandatoryHtml }} />
                : <p className="text-xs text-[#9CA3AF] italic">A carregar pré-visualização…</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <Link to="/leads" className="text-xs text-[#6B7280] hover:text-[#0F1829] hover:underline">← Ir para Leads</Link>
        <div className="flex items-center gap-2">
          <button onClick={handleTest} disabled={!isConfigured || testing || saving} data-testid="test-email-btn"
            className="px-4 py-2 text-sm font-medium border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
            {testing ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
            Enviar email de teste
          </button>
          <button onClick={handleSave} disabled={saving || testing} data-testid="save-config-btn"
            className="px-5 py-2 text-sm font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
            {saving ? <SpinnerGap size={14} className="animate-spin" /> : <FloppyDisk size={14} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
